/**
 * Энергия года и действия по местам (core-loop-v2 §3; план 2d, Task 2).
 *
 * Энергия — очки на год: база ENERGY_BASE, модификаторы отражают реальность:
 * здоровье < ENERGY_LOW_HEALTH → 2; возраст ≥ ENERGY_OLD_AGE → 2; школа до
 * ENERGY_SCHOOL_MAX_AGE → 2 (школа съедает время); ребёнок до
 * ENERGY_TODDLER_MAX_AGE → −1; хроническая болезнь → −1; не ниже ENERGY_MIN.
 * Тик пересчитывает энергию и обнуляет визиты на смене года.
 *
 * Действие (`ActionInput`) — место, дело и исход `MinigameOutcome`, который
 * считает пакет мест (@life/minigames). Применяется по порядку: нужны
 * энергия > 0 и меньше VISITS_MAX визитов в это место за год, иначе действие
 * отбрасывается (`rejected`). Убывающая отдача: второй визит в то же место —
 * половина чисел исхода (деньги, состояния, тяга), третий недоступен.
 * Флаги и запись журнала не масштабируются. Вход не мутируется.
 */
import { CONDITION_CHRONIC } from './health';
import { applyMinigame, type MinigameOutcome } from './minigames';
import type { Life, LogEntry, Stats } from './types';

export interface ActionInput {
  place: string;
  action: string;
  outcome: MinigameOutcome;
}

export const ENERGY_BASE = 3;
export const ENERGY_MIN = 1;
/** Слабое здоровье и старость: энергия не больше двух. */
export const ENERGY_REDUCED = 2;
export const ENERGY_LOW_HEALTH = 40;
export const ENERGY_OLD_AGE = 70;
/** Школа до этого возраста (не включая) оставляет два очка. */
export const ENERGY_SCHOOL_MAX_AGE = 15;
/** Ребёнок этого возраста или младше отнимает очко. */
export const ENERGY_TODDLER_MAX_AGE = 3;
/** Сколько визитов в одно место за год дают эффект; дальше — недоступно. */
export const VISITS_MAX = 2;
export const SECOND_VISIT_MULT = 0.5;

/** Энергия на год от текущего состояния жизни. */
export function energyFor(life: Life): number {
  let energy = ENERGY_BASE;
  if (life.stats.health < ENERGY_LOW_HEALTH) energy = Math.min(energy, ENERGY_REDUCED);
  if (life.age >= ENERGY_OLD_AGE) energy = Math.min(energy, ENERGY_REDUCED);
  if (life.inSchool && life.age < ENERGY_SCHOOL_MAX_AGE) energy = Math.min(energy, ENERGY_REDUCED);
  if (life.childrenAges.some((a) => a <= ENERGY_TODDLER_MAX_AGE)) energy -= 1;
  if (life.conditions.includes(CONDITION_CHRONIC)) energy -= 1;
  return Math.max(ENERGY_MIN, energy);
}

/** Сколько раз за год жизнь уже была в месте. */
export function visitsOf(life: Life, place: string): number {
  const raw = life.visits[place];
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : 0;
}

/** Множитель эффекта следующего визита: 1, затем 0,5, затем 0 (недоступно). */
export function visitMultiplier(life: Life, place: string): 0 | 0.5 | 1 {
  const n = visitsOf(life, place);
  if (n === 0) return 1;
  if (n < VISITS_MAX) return SECOND_VISIT_MULT;
  return 0;
}

function scaleOutcome(outcome: MinigameOutcome, mult: number): MinigameOutcome {
  if (mult === 1) return outcome;
  const scaled: MinigameOutcome = { ...outcome };
  if (outcome.moneyDelta !== undefined) scaled.moneyDelta = outcome.moneyDelta * mult;
  if (outcome.urgeDelta !== undefined) scaled.urgeDelta = outcome.urgeDelta * mult;
  if (outcome.stats) {
    const stats: Partial<Stats> = {};
    for (const [k, v] of Object.entries(outcome.stats)) if (typeof v === 'number') stats[k as keyof Stats] = v * mult;
    scaled.stats = stats;
  }
  return scaled;
}

/**
 * Применить действия по порядку. Отброшенные (нет энергии, место исчерпано)
 * ничего не меняют и считаются в `rejected`. Записи года — в `entries`;
 * в журнал их кладёт тик (или клиент при мгновенном отклике).
 */
export function applyActions(life: Life, actions: readonly ActionInput[]): { life: Life; entries: LogEntry[]; rejected: number } {
  let current = life;
  const entries: LogEntry[] = [];
  let rejected = 0;
  for (const action of actions) {
    const mult = visitMultiplier(current, action.place);
    if (current.energy <= 0 || mult === 0) {
      rejected += 1;
      continue;
    }
    const applied = applyMinigame(current, scaleOutcome(action.outcome, mult));
    entries.push(...applied.entries);
    current = {
      ...applied.life,
      energy: current.energy - 1,
      visits: { ...current.visits, [action.place]: visitsOf(current, action.place) + 1 },
    };
  }
  return { life: current, entries, rejected };
}
